/**
 * Auth Context — Manages auth state for the app (cookie session)
 *
 * Provides:
 * - useAuth: hook to access auth state + login/logout
 * - getStoredToken: read the persisted JWT (for api.ts outside the React tree)
 * - The shared AuthContext + persisted-state helpers the provider builds on
 *
 * AuthProvider itself lives in auth-provider.tsx so this file only exports
 * non-components (react-refresh/only-export-components).
 *
 * APP-W01a — server identity authority:
 * - Explicit identity states (verifying/authenticated/unauthenticated/
 *   unavailable/transitioning); compatibility booleans derive from them.
 * - Only a current, shape-validated /auth/me response establishes identity.
 * - Persisted user/org/token and public CSRF issuance never authenticate;
 *   the persisted-state helpers below are retained with compatible public
 *   signatures until entry wiring removes their callers (APP-W01b/W02).
 */

import { createContext, useContext } from 'react';

/**
 * Explicit identity states (APP-W01a):
 * - verifying: bootstrap probe in flight; nothing may render as authed
 * - authenticated: current server-confirmed identity
 * - unauthenticated: the server confirmed there is no session (401)
 * - unavailable: probe failed (timeout/malformed/network/5xx) — distinct
 *   from unauthenticated; never treated as either
 * - transitioning: an identity change is in flight (e.g. login completing
 *   while its server verification runs)
 */
export type AuthStatus =
  | 'verifying'
  | 'authenticated'
  | 'unauthenticated'
  | 'unavailable'
  | 'transitioning';


export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  platformRole?: string | null;
}

export interface AuthOrgEntitlements {
  scout: boolean;
  scheduler: boolean;
}

export interface AuthOrg {
  orgId: string;
  name: string;
  role: string;
  /** Absent on stale JWT-era localStorage entries; always present in /auth/login and /auth/me responses. */
  entitlements?: AuthOrgEntitlements;
}

export interface AuthState {
  token: string | null;
  user: AuthUser | null;
  org: AuthOrg | null;
  isAuthenticated: boolean;
}

export interface AuthContextValue extends AuthState {
  /**
   * True once auth state reflects the server (login, /auth/me revalidation,
   * or no cookie session to revalidate). Until then, state restored from
   * localStorage is only a paint-fast hint and may be stale.
   */
  authResolved: boolean;
  /**
   * APP-W01a explicit identity state. AuthProvider always supplies it.
   * Externally constructed contexts (test fixtures) may omit it; the root
   * gate then derives a conservative equivalent via resolveAuthStatus.
   */
  status?: AuthStatus;
  /** Cookie-only login: token may be null. Pass user + org returned by /auth/login or /auth/me. */
  login: (token: string | null, user: AuthUser, org: AuthOrg) => void;
  /**
   * APP-W01b: logout hides the identity synchronously and awaits the
   * one-shot server revocation; the resolved value reports the TRUTHFUL
   * revocation state — never a substituted success.
   */
  logout: () => Promise<LogoutRevocationState>;
  updateUser: (user: Partial<AuthUser>) => void;
  /**
   * Re-fetch /auth/me and overwrite user/org with the server response. Needed
   * after flows whose response org omits entitlements (accept-invite) — the
   * mount-time revalidation only runs once, so entitlement-gated UI would
   * otherwise stay wrong until a full reload.
   */
  revalidateAuth: () => Promise<void>;
}

/**
 * Root-gate view of the identity state. The provider-supplied explicit
 * status wins; contexts built without one (legacy fixtures) derive a
 * conservative equivalent from the compatibility booleans — never from any
 * persisted value.
 */
export function resolveAuthStatus(
  value: Pick<AuthContextValue, 'status' | 'isAuthenticated' | 'authResolved'>
): AuthStatus {
  if (value.status) return value.status;
  return value.authResolved
    ? value.isAuthenticated
      ? 'authenticated'
      : 'unauthenticated'
    : 'verifying';
}

/** Server-confirmed /auth/me payload (user + org, shape-validated). */
export interface ServerIdentityPayload {
  user: AuthUser;
  org: AuthOrg;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Shape validation for a /auth/me body. Anything that does not fully match
 * the current server payload shape fails closed (treated as unavailable) —
 * only a well-formed response establishes identity.
 */
export function isServerIdentityPayload(data: unknown): data is ServerIdentityPayload {
  if (!isRecord(data)) return false;
  const user = data.user;
  const org = data.org;
  if (!isRecord(user) || !isRecord(org)) return false;
  if (typeof user.userId !== 'string' || user.userId.length === 0) return false;
  if (typeof user.email !== 'string' || typeof user.name !== 'string') return false;
  if (typeof org.orgId !== 'string' || org.orgId.length === 0) return false;
  if (typeof org.name !== 'string' || typeof org.role !== 'string') return false;
  if (org.entitlements !== undefined) {
    if (!isRecord(org.entitlements)) return false;
    if (typeof org.entitlements.scout !== 'boolean') return false;
    if (typeof org.entitlements.scheduler !== 'boolean') return false;
  }
  return true;
}

export const AUTH_STORAGE_KEY = '11to1_auth';

/**
 * APP-W01b — truthful logout revocation report.
 * - revocation-confirmed: server responded ok to /auth/logout
 * - revocation-unconfirmed: the POST failed or was ambiguous; the client
 *   DOES NOT claim the server session ended (and never claims client
 *   cancellation stopped server work)
 * - session-already-absent: server-provable absence (401-shaped response
 *   for the CURRENT identity)
 */
export type LogoutRevocationState =
  | 'revocation-confirmed'
  | 'revocation-unconfirmed'
  | 'session-already-absent';

/**
 * APP-W01a: retained with a compatible public signature until entry wiring
 * (W01b/W02) removes its callers. It is NO LONGER used to initialise auth
 * state — a persisted record is a stale hint at most and never establishes
 * authentication.
 */
export function loadPersistedAuth(): AuthState {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.token && parsed.user && parsed.org) {
        return {
          token: parsed.token,
          user: parsed.user,
          org: parsed.org,
          isAuthenticated: true,
        };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { token: null, user: null, org: null, isAuthenticated: false };
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}

/**
 * Get the current auth token (for use in api.ts outside React tree)
 */
export function getStoredToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.token || null;
    }
  } catch {
    // Ignore
  }
  return null;
}
